import { Node } from "@tiptap/core";
import {
  CARD_TYPES,
  TYPE_LABELS,
  CARD_ICONS,
  CARD_STYLES,
  encodeCardClass,
  decodeCardClass,
  generateActionButtons,
  generateCardHTML,
  generateFormCardHTML,
  generateOrderStatusHTML,
  applyDeletedStyling,
  findCardWrapper,
  formatDocumentCardDescription,
} from "@/views/encounter-notes/utils/actionCardUtils";
// Default order status config (for freshly created orders before GraphQL fetch)
const DEFAULT_ORDER_STATUS = { label: 'New Order', color: '#43D4C3', bgColor: '#D5EFEE' };

// Helper: build extra fields object for encoding order/form-specific attrs
const buildExtra = (sampleId, orderStatus, formAttrs) => {
  const extra = {};
  if (sampleId) extra.sampleId = sampleId;
  if (orderStatus) extra.orderStatus = orderStatus;
  if (formAttrs) {
    if (formAttrs.dueDate) extra.dueDate = formAttrs.dueDate;
    if (formAttrs.isCompleted !== undefined) extra.isCompleted = formAttrs.isCompleted;
    if (formAttrs.formDescription) extra.formDescription = formAttrs.formDescription;
  }
  return Object.keys(extra).length ? extra : null;
};

// Helper: build order status badge render options
const getOrderStatusOptions = (statusInfo) => {
  if (!statusInfo) return {};
  const label = statusInfo.label || DEFAULT_ORDER_STATUS.label;
  const color = statusInfo.color || DEFAULT_ORDER_STATUS.color;
  const bgColor = statusInfo.bgColor || DEFAULT_ORDER_STATUS.bgColor;
  return { orderStatusHTML: generateOrderStatusHTML(label, color, bgColor) };
};

const CardActionNode = Node.create({
  name: 'cardAction',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      isDraft: true,
      getIsDraft: null,
    };
  },

  addAttributes() {
    return {
      type: { default: 'task' },
      id: { default: null },
      title: { default: '' },
      description: { default: '' },
      calendarId: { default: null },
      sampleId: { default: null },
      orderStatus: { default: null },
      dueDate: { default: null },
      isCompleted: { default: false },
      formDescription: { default: null },
      freshlyCreated: {
        default: false,
        rendered: false,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div.card-action-wrapper',
        getAttrs: (element) => {
          const className = element.className || '';
          const decoded = decodeCardClass(className);
          if (decoded) return decoded;
          return {
            type: element.getAttribute('data-type') || 'task',
            id: element.getAttribute('data-id') || null,
            title: element.getAttribute('data-title') || '',
            description: element.getAttribute('data-description') || '',
          };
        },
      },
    ];
  },

  renderHTML({ node }) {
    const { type, id, title, description, calendarId, sampleId, orderStatus, dueDate, isCompleted, formDescription } = node.attrs;
    const typeLabel = TYPE_LABELS[type] || type;
    const formAttrs = type === CARD_TYPES.FORM ? { dueDate, isCompleted, formDescription } : null;
    const dataClass = encodeCardClass(type, id, title, description, calendarId, buildExtra(sampleId, orderStatus, formAttrs));

    const btnStyle = CARD_STYLES.actionBtn;
    let actionButtons = [];

    if (this.options.isDraft) {
      if (type === CARD_TYPES.APPOINTMENT) {
        actionButtons = [
          ['span', { class: 'card-action-btn card-action-btn-edit', style: btnStyle }, 'Edit'],
          ['span', { class: 'card-action-btn card-action-btn-cancel', style: btnStyle }, 'Cancel Appointment'],
        ];
      } else if (type === CARD_TYPES.DOCUMENT) {
        actionButtons = [
          ['span', { class: 'card-action-btn card-action-btn-preview', style: btnStyle }, 'Preview'],
          ['span', { class: 'card-action-btn card-action-btn-delete', style: btnStyle }, 'Delete Document'],
        ];
      } else if (type === CARD_TYPES.ORDER) {
        actionButtons = [
          ['span', { class: 'card-action-btn card-action-btn-details', style: btnStyle }, 'See Details'],
        ];
      } else if (type === CARD_TYPES.FORM) {
        actionButtons = [
          ['span', { class: 'card-action-btn card-action-btn-details', style: btnStyle }, 'See Details'],
        ];
      } else {
        actionButtons = [
          ['span', { class: 'card-action-btn card-action-btn-edit', style: btnStyle }, 'Edit'],
          ['span', { class: 'card-action-btn card-action-btn-delete', style: btnStyle }, 'Delete Task'],
        ];
      }
    }

    const cardContent = [
      ['div', { class: 'card-action-header', style: CARD_STYLES.header },
        ['div', { class: `card-action-icon card-action-icon-${type}`, style: CARD_STYLES.icon }],
        ['div', { class: 'card-action-title', style: CARD_STYLES.title }, `${typeLabel}: ${title || 'Untitled'}`],
      ],
      ['div', { class: 'card-action-description', style: CARD_STYLES.description }, description || 'No description provided.'],
    ];

    if (actionButtons.length > 0) {
      cardContent.push(['div', { class: 'card-action-actions', style: CARD_STYLES.actions }, ...actionButtons]);
    }

    return ['div', { class: `card-action-wrapper ${dataClass}`, contenteditable: 'false', style: CARD_STYLES.wrapper }, [
      'div', { class: 'card-action-bubble', style: CARD_STYLES.bubble }, [
        'div', { class: 'card-action-content', style: CARD_STYLES.content },
        ...cardContent,
      ],
    ]];
  },

  addKeyboardShortcuts() {
    return {
      Backspace: ({ editor }) => {
        const { selection } = editor.state;
        if (selection.node?.type.name === this.name) return true;
        const { $anchor } = selection;
        const before = $anchor.nodeBefore;
        if (before?.type.name === this.name && $anchor.parentOffset === $anchor.pos - $anchor.start()) return true;
        return false;
      },
      Delete: ({ editor }) => {
        const { selection } = editor.state;
        if (selection.node?.type.name === this.name) return true;
        const { $anchor } = selection;
        const after = $anchor.nodeAfter;
        if (after?.type.name === this.name) return true;
        return false;
      },
    };
  },

  addNodeView() {
    const extensionOptions = this.options;
    return ({ node, editor }) => {
      const { type, id, title, description, calendarId, sampleId, orderStatus, dueDate, isCompleted, formDescription, formLink, formId } = node.attrs;

      const typeLabel = TYPE_LABELS[type] || type;
      const iconSvg = CARD_ICONS[type] || CARD_ICONS[CARD_TYPES.TASK];

      const isDraft = extensionOptions.getIsDraft ? extensionOptions.getIsDraft() : extensionOptions.isDraft;
      const getActionButtons = () => isDraft ? generateActionButtons(type, id, calendarId) : '';
      const formAttrs = type === CARD_TYPES.FORM ? { dueDate, isCompleted, formDescription, formLink, formId } : null;
      const dataClass = encodeCardClass(type, id, title, description, calendarId, buildExtra(sampleId, orderStatus, formAttrs));

      const dom = document.createElement('div');
      dom.className = `card-action-wrapper ${dataClass}`;
      dom.setAttribute('contenteditable', 'false');
      dom.style.cssText = CARD_STYLES.wrapper;

      let displayDescription = description || 'Loading...';
      let displayTitle = title || 'Untitled';

      const renderCard = (cardTitle, cardDescription, options = {}) => {
        dom.innerHTML = generateCardHTML(type, cardTitle, cardDescription, iconSvg, getActionButtons(), options);
      };

      // Form cards use a different HTML layout
      const renderFormCard = (formTitle, formDue, completed, formDesc) => {
        dom.innerHTML = generateFormCardHTML(formTitle, formDue, completed, formDesc, iconSvg, getActionButtons());
      };

      // Initial render
      if (type === CARD_TYPES.FORM) {
        renderFormCard(
          displayTitle,
          dueDate || '',
          isCompleted || false,
          formDescription || 'This is a form completion task for patient.',
        );
      } else {
        renderCard(displayTitle, displayDescription, type === CARD_TYPES.ORDER ? getOrderStatusOptions(DEFAULT_ORDER_STATUS) : {});
      }

      // Async fetch to refresh order card data on note reload
      if (type === CARD_TYPES.ORDER && id) {
        import('@/views/encounter-notes/utils/encounterNotes').then(({ fetchOrderCardDetails }) => {
          const doFetch = fetchOrderCardDetails
            ? fetchOrderCardDetails(id, sampleId)
            : Promise.resolve(null);
          doFetch.then((result) => {
            if (result) {
              dom._orderData = result;
              displayDescription = result.description;
            }
            const statusInfo = result
              ? { label: result.statusLabel, color: result.statusColor, bgColor: result.statusBgColor }
              : DEFAULT_ORDER_STATUS;
            renderCard(displayTitle, displayDescription, getOrderStatusOptions(statusInfo));
          }).catch(() => {
            renderCard(displayTitle, displayDescription, getOrderStatusOptions(DEFAULT_ORDER_STATUS));
          });
        });
      }

      // Async fetch to refresh form card status on note reload
      if (type === CARD_TYPES.FORM && id) {
        import('@/views/encounter-notes/utils/encounterNotes').then(({ fetchFormCardStatus }) => {
          if (!fetchFormCardStatus) return;
          fetchFormCardStatus(id).then((result) => {
            if (result) {
              renderFormCard(
                displayTitle,
                dueDate || '',
                result.isCompleted,
                formDescription || 'This is a form completion task for patient.',
              );
            }
          }).catch(() => {
            // Keep initial render on error
          });
        });
      }

      if ((type === CARD_TYPES.TASK || type === CARD_TYPES.DOCUMENT || type === CARD_TYPES.APPOINTMENT) && id) {
        import('@/views/encounter-notes/utils/encounterNotes').then(({ checkCardStatus, formatAppointmentDescription }) => {
          checkCardStatus(type, id, calendarId).then(({ isDeleted, data }) => {
            if (isDeleted) {
              setTimeout(() => {
                const wrapper = findCardWrapper('.editor-input', type, id);
                if (wrapper) {
                  applyDeletedStyling(wrapper, type, () => wrapper.remove(), isDraft);
                }
              }, 0);
              return;
            }

            if (type === CARD_TYPES.TASK && data) {
              renderCard(data.title || displayTitle, data.description || 'No description provided.');
              dom._taskData = data;
            } else if (type === CARD_TYPES.DOCUMENT && data) {
              dom._documentData = { id };
              if (!description) {
                renderCard(
                  data.fileName || data.file_name || displayTitle,
                  formatDocumentCardDescription(data),
                );
              }
            } else if (type === CARD_TYPES.APPOINTMENT && data) {
              const fetchedDescription = formatAppointmentDescription(data);
              const fetchedTitle = data.event_title || displayTitle;
              renderCard(fetchedTitle, fetchedDescription);
              dom._eventData = data;
            }
          });
        });
      }

      return { dom };
    };
  },
});

export default CardActionNode;
